import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	DistributiveOmit,
	getObjectValues,
	Modal,
	Row,
	stringToLocalDate,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import {
	FieldConditional,
	FieldElse,
	FieldIf,
	Form,
	FormEditButtonPair,
	FormState,
	Label,
	settings,
	SubmitButton,
	useInternalField,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import {
	CalendarEventCreateMutation,
	CalendarEventDetailQuery,
	CalendarEventUpdateMutation,
	useInternalPortalsViewerInfoQuery,
} from "../../../Utilities";
import { CalendarEventModal, ModalCalendarEvent } from "../../Components/CalendarEvent";
import { CalendarEventFormState, CalendarEventFormValues } from "../../FormUtilities";
import { UserUtilities } from "../../Utilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";
import styles from "./form.module.css";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

const timeFieldNames: (keyof CalendarEventFormValues.Base)[] = ["startTime", "endTime"];
const dateFieldNames: (keyof CalendarEventFormValues.Base)[] = ["startDate", "endDate"];

export type ControlledCreateProps = Omit<
	CreateProps<CalendarEventCreateMutation["createCalendarEvent"]>,
	"formState" | "create"
> & {
	formStateArgs?: CalendarEventFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the CalendarEvent model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<CalendarEventFormValues.Create>;
	create: CreateFunction<CalendarEventFormValues.Create, T>;
	onSuccess: (formState: FormState<CalendarEventFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<CalendarEventFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the CalendarEvent model using the given form state.
 */
export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	columnClassName,
	columnStyle,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "calendarEvent.create" + (formNameSuffix ? "." + formNameSuffix : "");

	const { formValues } = formState;
	const { data } = useInternalPortalsViewerInfoQuery();
	const viewer = data?.viewer;
	const previewCalendarEvent = React.useMemo<ModalCalendarEvent>(
		() => ({
			...formValues,
			id: "preview",
			author: viewer ?? UserUtilities.placeholderCurrentUser,
			imageURL: formValues.imageKey
				? settings.uploads.fileInfoToURL(formValues.imageKey, formValues.imageContentType)
				: "",
			mediaAttachments: formValues.mediaAttachments.map((e, i) => ({
				...e,
				id: `preview-${i}`,
				fileURL: e.fileKey ? settings.uploads.fileInfoToURL(e.fileKey, e.fileContentType) : "",
			})),
			sharingPolicy: {
				id: "preview",
				isPublic: formValues.sharingPolicy[0].isPublic,
			},
			calendarEventResponses: [],
			createdAt: new Date().toISOString(),
		}),
		[viewer, formValues],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
				<Form.Column className={columnClassName} style={columnStyle}>
					<CreateFields formState={formState} />

					<Row justify="spaced-end">
						<Button variant="secondary" onClick={openModal}>
							Preview
						</Button>
						<SubmitButton
							submissionStatus={formState.submissionStatus}
							onClick={formState.onSubmitWrapper(onSubmit)}
							disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
						>
							Create Event
						</SubmitButton>
					</Row>
				</Form.Column>
			</Form>

			<Modal.If condition={isModalOpen}>
				<CalendarEventModal
					calendarEvent={previewCalendarEvent}
					noICalDownload
					noRSVPForm
					withBackButton
					isPreview
					backButtonText="Back to Form"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CreateFieldsProps = {
	formState: FormState<CalendarEventFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	useInternalField(formState, "isMultiDay");

	useScheduleFields(formState);

	return (
		<React.Fragment>
			<Field.Image formState={formState} style={{ width: "100%" }} />

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.CalendarBlank />
					</span>
					<Field.Name formState={formState} style={{ width: "100%" }} />
				</Row>
				<div className={styles["form__detail__details"]}>
					<Field.Description
						formState={formState}
						noHeader
						placeholder="Describe the event..."
						style={{ width: "100%" }}
					/>
				</div>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="flex-start" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.Clock />
					</span>

					<Row justify="spaced-start" withVerticalSpacing overflow="wrap" style={{ width: "100%" }}>
						<FieldConditional>
							<FieldIf formState={formState} names={dateFieldNames} condition={formState.formValues.isAllDay}>
								<Field.StartDate formState={formState} style={{ flexGrow: 1, width: "auto" }} />
								<Field.EndDate formState={formState} style={{ flexGrow: 1, width: "auto" }} />
							</FieldIf>
							<FieldElse formState={formState} names={timeFieldNames}>
								<Field.StartTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
								<Field.EndTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
							</FieldElse>
						</FieldConditional>
					</Row>
				</Row>
				<div className={styles["form__detail__details"]}>
					<Field.IsAllDay formState={formState} />
				</div>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.MapPin />
					</span>
					<Field.Location formState={formState} style={{ width: "100%" }} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.Users />
					</span>
					<Field.SharingPolicy.SubForm formState={formState} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.EnvelopeSimple />
					</span>
					<Field.AllowsResponses formState={formState} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.File />
					</span>
					<Label>Attachments</Label>
				</Row>
				<div className={styles["form__detail__details"]}>
					<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />
				</div>
			</Column>
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<CalendarEventUpdateMutation["updateCalendarEvent"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<CalendarEventFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the CalendarEvent model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, calendarEvent: props.calendarEvent });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	calendarEvent: CalendarEventDetailQuery["calendarEvent"];
	formState: FormState<CalendarEventFormValues.Detail>;
	update: UpdateFunction<CalendarEventFormValues.Detail, T>;
	onSuccess: (formState: FormState<CalendarEventFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<CalendarEventFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the CalendarEvent model using the given form state.
 */
export function Detail<T>({ formState, calendarEvent, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `calendarEvent.detail.${calendarEvent.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} calendarEvent={calendarEvent} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<CalendarEventFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the CalendarEvent model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, calendarEvent: props.calendarEvent });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	calendarEvent: CalendarEventDetailQuery["calendarEvent"];
	formState: FormState<CalendarEventFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the CalendarEvent model using the given form state.
 */
export function ReadOnly({ formState, calendarEvent, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} calendarEvent={calendarEvent} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	calendarEvent: CalendarEventDetailQuery["calendarEvent"];
	formState: FormState<CalendarEventFormValues.Detail>;
};

function DetailFields({ formState, calendarEvent }: DetailFieldsProps) {
	useInternalField(formState, "isMultiDay");
	useInternalField(formState, "authorID");
	useInternalField(formState, "createdAt");

	useScheduleFields(formState);

	return (
		<React.Fragment>
			<Field.Image formState={formState} url={calendarEvent.imageURL} style={{ width: "100%" }} />

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.CalendarBlank />
					</span>
					<Field.Name formState={formState} style={{ width: "100%" }} />
				</Row>
				<div className={styles["form__detail__details"]}>
					<Field.Description
						formState={formState}
						noHeader
						placeholder="Describe the event..."
						style={{ width: "100%" }}
					/>
				</div>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="flex-start" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.Clock />
					</span>

					<Row justify="spaced-start" withVerticalSpacing overflow="wrap" style={{ width: "100%" }}>
						<FieldConditional>
							<FieldIf formState={formState} names={dateFieldNames} condition={formState.formValues.isAllDay}>
								<Field.StartDate formState={formState} style={{ flexGrow: 1, width: "auto" }} />
								<Field.EndDate formState={formState} style={{ flexGrow: 1, width: "auto" }} />
							</FieldIf>
							<FieldElse formState={formState} names={timeFieldNames}>
								<Field.StartTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
								<Field.EndTime formState={formState} style={{ flexGrow: 1, width: "auto" }} />
							</FieldElse>
						</FieldConditional>
					</Row>
				</Row>
				<div className={styles["form__detail__details"]}>
					<Field.IsAllDay formState={formState} />
				</div>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]}>
						<Icon.MapPin />
					</span>
					<Field.Location formState={formState} style={{ width: "100%" }} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.Users />
					</span>
					<Field.SharingPolicy.Detail sharingPolicy={calendarEvent.sharingPolicy} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.EnvelopeSimple />
					</span>
					<Field.AllowsResponses formState={formState} />
				</Row>
			</Column>

			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="spaced-start" align="center" className={styles["form__detail--main"]}>
					<span className={styles["form__icon-container"]} style={{ marginTop: 0 }}>
						<Icon.File />
					</span>
					<Label>Attachments</Label>
				</Row>
				<div className={styles["form__detail__details"]}>
					<MediaAttachmentCustomForm.ControlledMultiEditForm
						calendarEventID={calendarEvent.id}
						mediaAttachments={calendarEvent.mediaAttachments}
					/>
				</div>
			</Column>
		</React.Fragment>
	);
}

function useScheduleFields(
	formState: FormState<
		Pick<CalendarEventFormValues.Base, "isAllDay" | "startDate" | "endDate" | "startTime" | "endTime">
	>,
) {
	const {
		formValues: { isAllDay, startDate, endDate, startTime, endTime },
		formSetFunctions: { startDate: setStartDate, endDate: setEndDate, startTime: setStartTime, endTime: setEndTime },
	} = formState;

	const lastStartDateRef = React.useRef(startDate);
	React.useEffect(() => {
		if (startDate) {
			lastStartDateRef.current = startDate;
		}
	}, [startDate]);
	const lastEndDateRef = React.useRef(endDate);
	React.useEffect(() => {
		if (endDate) {
			lastEndDateRef.current = endDate;
		}
	}, [endDate]);
	const lastStartTimeRef = React.useRef(startTime);
	React.useEffect(() => {
		if (startTime) {
			lastStartTimeRef.current = startTime;
		}
	}, [startTime]);
	const lastEndTimeRef = React.useRef(endTime);
	React.useEffect(() => {
		if (endTime) {
			lastEndTimeRef.current = endTime;
		}
	}, [endTime]);

	React.useEffect(() => {
		if (isAllDay) {
			if (lastStartTimeRef.current) {
				setStartDate(toLocalDateString(lastStartTimeRef.current));
			}
			if (lastEndTimeRef.current) {
				setEndDate(toLocalDateString(lastEndTimeRef.current));
			}
		} else {
			if (lastStartDateRef.current) {
				setStartTime(stringToLocalDate(lastStartDateRef.current, "day").toISOString());
			}
			if (lastEndDateRef.current) {
				setEndTime(stringToLocalDate(lastEndDateRef.current, "day").toISOString());
			}
		}
	}, [isAllDay, setStartDate, setEndDate, setStartTime, setEndTime]);
}
